import { CircularAvatar, CircularAvatarSize } from "@core/components/_original/CircularAvatar";
import { StDropdown } from "@core/components/_original/StDropdown";
import { StIcon } from "@core/components/icons";
import { parsePromptTextMentions } from "@core/lib/parsePromptTextMentions";
import { PromptAssetCTA, PromptCollectionCTA } from "@core/screens/modals/ImprovePromptModal";
import { SaveToPromptLibraryModal } from "@core/screens/modals/SaveToPromptLibraryModal";
import type { curator } from "@repo/client";
import { TbBook, TbCopy, TbDots, TbFile, TbLink, TbMinus, TbPlus } from "solid-icons/tb";
import { type Component, For, Match, Switch, createEffect, createMemo, createSignal } from "solid-js";

export type TextInstructionUnitV1Props = {
  message: curator.MessagePromptV2;
  class?: string;
};

export const TextPromptUnitV1 = (props: TextInstructionUnitV1Props) => {
  const [seeAll, setSeeAll] = createSignal(false);
  const [saveToPromptLibraryModalOpen, setSaveToPromptLibraryModalOpen] = createSignal(false);

  // Collection mentions are sent to the server as @[collection_id]"Collection name"
  // We need to replace them with the collection label when displaying the prompt in the thread
  const formatPrompt = createMemo(() => {
    const prompt = props.message.prompt.replaceAll("\n\n", "\n");
    return parsePromptTextMentions(prompt);
  });

  return (
    <>
      <div class="relative">
        <button
          type="button"
          class="bg-surface rounded-lg px-2 py-2 flex items-center gap-3 mb-8 text-on-surface"
          onClick={() => {
            setSeeAll((s) => !s);
          }}
        >
          <div class="flex items-center self-start" style={{ height: "3rem" }}>
            <CircularAvatar
              userId={props.message.createdBy}
              size={CircularAvatarSize.Mini}
              fullName="Guest"
              class="ring-background-decoration flex-shrink-0"
            />
          </div>

          <div
            class="overflow-hidden"
            style={{ "max-height": seeAll() ? "1000rem" : "4.52rem", transition: "max-height 300ms ease-in-out" }}
          >
            <p class="whitespace-pre-line font-medium">
              <For each={formatPrompt()}>
                {(segment) => (
                  <Switch>
                    <Match when={segment.type === "text" && segment.text}>{(s) => <span>{s()}</span>}</Match>
                    <Match when={segment.type === "collection" && segment}>
                      {(s) => <PromptCollectionCTA id={s().id} name={s().name} />}
                    </Match>
                    <Match when={segment.type === "asset" && segment}>
                      {(s) => <PromptAssetCTA id={s().id} name={s().name} />}
                    </Match>
                  </Switch>
                )}
              </For>
            </p>
          </div>
          <div class="w-11 flex-shrink-0" aria-hidden />
        </button>

        <div class="absolute top-2 right-2 text-on-surface">
          <StDropdown
            theme="default"
            items={[
              {
                kind: "item",
                content: "Save to Prompt Library",
                icon: TbBook,
                props: {
                  onSelect: () => {
                    setTimeout(() => {
                      setSaveToPromptLibraryModalOpen(true);
                    }, 250);
                  },
                },
              },
              {
                kind: "item",
                content: "Share prompt as link",
                icon: TbLink,
                props: {
                  onSelect: () => {
                    navigator.clipboard.writeText(
                      `https://${APP_DOMAIN}?prompt=${encodeURIComponent(props.message.prompt)}`,
                    );
                  },
                },
              },
              {
                kind: "item",
                content: "Copy",
                icon: TbCopy,
                props: {
                  onSelect: () => {
                    navigator.clipboard.writeText(props.message.prompt);
                  },
                },
              },
              {
                kind: "item",
                content: seeAll() ? "Minimize" : "Expand",
                icon: seeAll() ? TbMinus : TbPlus,
                props: {
                  onSelect: () => {
                    setSeeAll((s) => !s);
                  },
                },
              },
            ]}
            trigger={{
              class: "hover:bg-surface transition-colors flex items-center justify-center rounded-full min-w-7 min-h-7",
              onClick: (e) => {
                e.stopPropagation();
                e.preventDefault();
              },
            }}
          >
            <StIcon icon={TbDots} size={20} stroke-width={3} />
          </StDropdown>
        </div>
      </div>
      <SaveToPromptLibraryModal
        open={saveToPromptLibraryModalOpen()}
        setOpen={setSaveToPromptLibraryModalOpen}
        initialPrompt={props.message.prompt}
      />
    </>
  );
};
